// Credit cards and reimbursements — what got spent on a card or out of someone's own
// pocket, which job it was for, and whether the statement has been matched against it.
//
// These are purchase orders of a different kind, not a table of their own: that is what
// makes them appear on the job's PO section and count toward what the job has spent,
// without any of that being taught about them separately.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { apiFetch, API_BASE } from '../../services/apiClient';

const money = n => (n == null || n === '') ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const shortDate = s => (s ? new Date(String(s).length <= 10 ? s + 'T12:00:00' : s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');
const photoUrl = f => `${API_BASE.replace('/api', '')}/uploads/${f}`;

export default function AdminCardCharges() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [sort, setSort] = useState('person');     // person | job | date
  const [show, setShow] = useState('open');       // open | all
  const [busy, setBusy] = useState('');
  const [photo, setPhoto] = useState(null);
  // The same search as the PO list (Pat, Sep 28 2026): words, a From / To range on the
  // day it was written, and an order inside each group.
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [order, setOrder] = useState('newest');   // newest | oldest | amount | vendor

  const load = useCallback(() => {
    apiFetch('/card-charges').then(r => setRows(Array.isArray(r) ? r : []))
      .catch(e => setErr(e?.error || 'Could not load the charges.'));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function toggle(c) {
    setBusy(c.id);
    try {
      const upd = await apiFetch(`/card-charges/${c.id}/reconciled`, {
        method: 'POST', body: JSON.stringify({ reconciled: !c.reconciled_at }),
      });
      setRows(rs => rs.map(x => (x.id === c.id ? { ...x, ...upd } : x)));
    } catch (e) { setErr(e?.error || 'Could not save that.'); }
    setBusy('');
  }

  if (err && !rows) return <AdminLayout><div style={{ padding: 30, color: '#b91c1c' }}>{err}</div></AdminLayout>;
  if (!rows) return <AdminLayout><div style={{ padding: 30, color: '#6b7280' }}>Loading…</div></AdminLayout>;

  const open = rows.filter(r => !r.reconciled_at);
  const outstanding = open.reduce((s, r) => s + Number(r.cost || 0), 0);
  const q = search.trim().toLowerCase();
  const dayOf = r => {
    const d = new Date(String(r.created_at).length <= 10 ? r.created_at + 'T12:00:00' : r.created_at);
    return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const rangeOn = !!(from || to);
  const shown = (show === 'open' ? open : rows).filter(r => {
    if (q && !`${r.description || ''} ${r.vendor || ''} ${r.project_number || ''} ${r.project_name || ''} ${r.paid_by_name || ''} ${r.cost ?? ''}`.toLowerCase().includes(q)) return false;
    if (!rangeOn) return true;
    const day = dayOf(r);
    return (!from || day >= from) && (!to || day <= to);
  });
  const orderRows = list => [...list].sort((a, b) => {
    if (order === 'amount') return Number(b.cost || 0) - Number(a.cost || 0);
    if (order === 'vendor') return String(a.vendor || '').localeCompare(String(b.vendor || '')) || String(b.created_at || '').localeCompare(String(a.created_at || ''));
    const c = String(a.created_at || '').localeCompare(String(b.created_at || ''));
    return order === 'oldest' ? c : -c;
  });
  const shownTotal = shown.reduce((s, r) => s + Number(r.cost || 0), 0);

  // Grouped by whichever way you're reading it. Date is one flat list.
  const groups = [];
  if (sort === 'date') groups.push({ key: 'all', label: null, rows: orderRows(shown) });
  else {
    const keyOf = r => (sort === 'person'
      ? (r.paid_by_name || 'Unknown')
      : `${r.project_number || '—'}${r.project_name ? ' · ' + r.project_name : ''}`);
    for (const r of shown) {
      const k = keyOf(r);
      let g = groups.find(x => x.key === k);
      if (!g) groups.push(g = { key: k, label: k, rows: [] });
      g.rows.push(r);
    }
    groups.sort((a, b) => String(a.key).localeCompare(String(b.key)));
    for (const g of groups) g.rows = orderRows(g.rows);
  }

  const seg = on => ({
    padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 800,
    background: on ? '#0e7490' : '#fff', color: on ? '#fff' : '#6b7280',
  });

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">💳 Credit Cards &amp; Reimbursements</h1>
          <p className="admin-page-sub">
            {open.length} not yet matched to a statement · {money(outstanding)} outstanding
          </p>
        </div>
      </div>

      <div className="admin-card" style={{ padding: 12, marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Group by</span>
        <div style={{ display: 'flex', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          {[['person', 'Person'], ['job', 'Job'], ['date', 'Date']].map(([k, l]) => (
            <button key={k} onClick={() => setSort(k)} style={seg(sort === k)}>{l}</button>
          ))}
        </div>
        <div style={{ display: 'flex', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          {[['open', 'Not matched'], ['all', 'Everything']].map(([k, l]) => (
            <button key={k} onClick={() => setShow(k)} style={seg(show === k)}>{l}</button>
          ))}
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Order</span>
        <div style={{ display: 'flex', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          {[['newest', 'Newest'], ['oldest', 'Oldest'], ['amount', 'Amount'], ['vendor', 'Vendor']].map(([k, l]) => (
            <button key={k} onClick={() => setOrder(k)} style={seg(order === k)}>{l}</button>
          ))}
        </div>
        {err && <span style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 700 }}>{err}</span>}
        <div style={{ flexBasis: '100%', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="field-input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search what it was, vendor, job, person, amount…" style={{ flex: 1, minWidth: 220 }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Written between</span>
          <input type="date" className="field-input" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} style={{ width: 160 }} title="From this day" />
          <span style={{ fontSize: 13, color: '#6b7280' }}>and</span>
          <input type="date" className="field-input" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} style={{ width: 160 }} title="Up to and including this day" />
          {(rangeOn || q) && (
            <>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>{shown.length} charge{shown.length === 1 ? '' : 's'} · {money(shownTotal)}</span>
              <button onClick={() => { setFrom(''); setTo(''); setSearch(''); }} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 20, padding: '5px 11px', fontSize: 12, fontWeight: 700, color: '#374151', cursor: 'pointer' }}>× Clear</button>
            </>
          )}
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="admin-card" style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
          {rows.length === 0 ? 'Nothing on a card yet.' : (q || rangeOn) ? 'Nothing matches that search.' : 'Everything is matched to a statement.'}
        </div>
      ) : groups.map(g => (
        <div key={g.key} style={{ marginBottom: 18 }}>
          {g.label && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '0 2px 8px' }}>
              <span style={{ fontSize: 13.5, fontWeight: 800, color: '#111' }}>{g.label}</span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>
                {g.rows.length} · {money(g.rows.reduce((s, r) => s + Number(r.cost || 0), 0))}
              </span>
            </div>
          )}
          <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
            {g.rows.map((c, i) => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
                borderTop: i ? '1px solid #f3f4f6' : 'none', background: c.reconciled_at ? '#fafafa' : '#fff' }}>
                {/* Ticking it off is the whole job on this screen, so it leads. */}
                <button onClick={() => toggle(c)} disabled={busy === c.id}
                  title={c.reconciled_at ? `Matched by ${c.reconciled_by_name || 'someone'} — press to undo` : 'Match this to the statement'}
                  style={{ width: 24, height: 24, flexShrink: 0, borderRadius: 6, cursor: 'pointer',
                    border: `2px solid ${c.reconciled_at ? '#0e7490' : '#d1d5db'}`,
                    background: c.reconciled_at ? '#0e7490' : '#fff',
                    color: '#fff', fontSize: 15, fontWeight: 800, lineHeight: 1, padding: 0 }}>
                  {c.reconciled_at ? '✓' : ''}
                </button>

                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13.5, color: '#111', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.description || 'No description'}
                  </div>
                  <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2 }}>
                    {sort !== 'job' && (
                      <button onClick={() => navigate(`/admin/jobs/${c.project_id}`)}
                        style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: '#1d4ed8', cursor: 'pointer' }}>
                        {c.project_number || 'job'}
                      </button>
                    )}
                    {sort !== 'job' && ' · '}
                    {sort !== 'person' && `${c.paid_by_name || 'Unknown'} · `}
                    {shortDate(c.created_at)}
                    {c.vendor ? ` · ${c.vendor}` : ''}
                  </div>
                </div>

                {/* Out of pocket is the one that needs paying back, so it gets the badge. */}
                {c.paid_with === 'own_money' && (
                  <span style={{ fontSize: 10.5, fontWeight: 800, color: '#92400e', background: '#fffbeb',
                    border: '1px solid #fde68a', borderRadius: 20, padding: '2px 9px', whiteSpace: 'nowrap' }}>
                    owed back
                  </span>
                )}

                {c.receipt_filename && (
                  <button onClick={() => setPhoto(photoUrl(c.receipt_filename))}
                    style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 7, padding: '4px 9px',
                      fontSize: 11.5, fontWeight: 700, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    🧾 Receipt
                  </button>
                )}

                <span style={{ fontSize: 14, fontWeight: 800, color: '#111', whiteSpace: 'nowrap', minWidth: 78, textAlign: 'right' }}>
                  {money(c.cost)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}

      {photo && (
        <div onClick={() => setPhoto(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 6000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, cursor: 'zoom-out' }}>
          <img src={photo} alt="Receipt" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} />
        </div>
      )}
    </AdminLayout>
  );
}
